import React, {useEffect, useRef, useState} from "react";
import {Button, Card, Col, Empty, Row, Spin, Tooltip, Typography} from "antd";
import dayjs from "dayjs";

import {getCreditCards, updateCreditCard} from "../../../helpers/creditCard";
import {messageType, showMessages} from "../../../helpers/message";

import {cardsGridColumn} from "../../../assets/styles";
import {black, white} from "../../../assets/colors";
import "./StoredCreditCardCardStaff.less";

const {Text} = Typography;

const StoredCreditCardCardStaff = ({toggleRefetchStoredCard}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);
    //region Data fetching
    const [loading, setLoading] = useState(true);
    const [creditCards, setCreditCards] = useState(undefined);
    const [refresh, setRefresh] = useState(true);

    useEffect(() => {
        async function fetchCreditCards() {
            try {
                setLoading(true);

                const response = await getCreditCards({});
                setCreditCards(response.data);
                setLoading(false);
            } catch (e) {
                showMessages(e.message, messageType.error);
                setLoading(false);
            }
        }

        if (refresh === true) {
            fetchCreditCards();
            setRefresh(false);
        }
    }, [refresh]);

    //endregion

    //region Credit Card component

    // Return different component based on credit cards data
    const getRenderComponent = () => {
        let component;

        if (creditCards === undefined) {
            component = getUndefinedCCComponent();
        } else if (creditCards.length === 0) {
            component = getNoCCComponent();
        } else {
            component = getCCComponent();
        }

        return component;
    }

    const getUndefinedCCComponent = () => {
        return <Card/>;
    }

    const getNoCCComponent = () => {
        return <Empty
            className="stored-card-empty"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span style={{color: black}}>You currently don't have a credit card on file. If you'd like, you can add one below!</span>}
        >
            <Tooltip title="Staff does not have the ability to add a credit card to an account.">
                <Button disabled>
                    Add a credit card
                </Button>
            </Tooltip>
        </Empty>
    }

    const getCCComponent = () => {
        return creditCards.map(creditCard => {
            const expiryDate = dayjs(creditCard.expiry);
            const expiry = expiryDate.format('MM') + ' / ' + expiryDate.format('YY');
            const cardInstitution = creditCard.iin;

            const type = creditCard.type.toUpperCase();
            const cardText = cardInstitution + '******' + creditCard.l4d;

            return <Card
                key={creditCard.l4d}
                title="Credit Card"
            >
                <Row>
                    <Col xs={12} lg={8}>
                        <Text strong>Card Type:</Text>
                    </Col>
                    <Col xs={12} lg={16}>{type}</Col>
                </Row>
                <Row>
                    <Col xs={12} lg={8}>
                        <Text strong>Card Number:</Text>
                    </Col>
                    <Col xs={12} lg={16}>{cardText}</Col>
                </Row>
                <Row>
                    <Col xs={12} lg={8}>
                        <Text strong>Expiry Date:</Text>
                    </Col>
                    <Col xs={12} lg={16}>
                        {expiry}
                    </Col>
                </Row>
                <Row className="authorized-message-padding">
                    <Col span={24}>
                        This member authorized their card for automatic payments.
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <Tooltip title="Staff does not have the ability to update a credit card for an account.">
                            <Button disabled>
                                Update Card
                            </Button>
                        </Tooltip>
                    </Col>
                    <Col span={12}>
                        <Button type="primary" onClick={() => removeCreditCard(creditCard.id)}>
                            Remove Card
                        </Button>
                    </Col>
                </Row>
            </Card>
        })
    }

    const removeCreditCard = async (id) => {
        const abortSignal = abortSignalRef.current;

        try {
            setLoading(true);

            await updateCreditCard(id, {isPrimary: false}, abortSignal);

            setRefresh(true);
            toggleRefetchStoredCard(true);
            setLoading(false);
        } catch (e) {
            showMessages(e.message, messageType.error);
            setLoading(false);
        }
    }

    //endregion
    //endregion

    return (
        <Spin spinning={loading}>
            <Row>
                <Col {...cardsGridColumn} style={{backgroundColor: white}}>
                    {
                        getRenderComponent()
                    }
                </Col>
            </Row>
        </Spin>
    )
}

export default StoredCreditCardCardStaff;
